import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getCheckoutStatus } from "../../api/CheckoutStatus";
import SEO from "../../components/SEO";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";
import { SITE_URL } from "../../config/site";
import { useCart } from "../../cart/CartContext";
import "./SuccessPage.css";

export default function SuccessPage() {
  const { clearCart } = useCart();
  const [paymentStatus, setPaymentStatus] = useState("checking");

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const orderId = query.get("order_id") || "";
    const sessionId = query.get("session_id") || "";
    const controller = new AbortController();

    if (!orderId || !sessionId) {
      setPaymentStatus("error");
      return () => controller.abort();
    }

    getCheckoutStatus({
      orderId,
      sessionId,
      signal: controller.signal,
    })
      .then((data) => {
        if (data.status === "confirmed") {
          clearCart();
          setPaymentStatus("confirmed");
          return;
        }

        if (data.status === "pending" || data.status === "review") {
          setPaymentStatus("pending");
          return;
        }

        setPaymentStatus("error");
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          setPaymentStatus("error");
        }
      });

    return () => controller.abort();
  }, [clearCart]);

  const content = {
    checking: {
      icon: "…",
      title: "Comprobando tu pago",
      message:
        "Estamos verificando la confirmación de Stripe. Este proceso suele tardar solo unos segundos.",
      note: "No cierres esta página mientras terminamos la comprobación.",
    },
    confirmed: {
      icon: "✓",
      title: "Pedido recibido correctamente",
      message:
        "Hemos recibido tu pago y tus archivos. Prepararemos tu pedido lo antes posible.",
      note: "Recibirás la información del pedido en tu email.",
    },
    pending: {
      icon: "…",
      title: "Tu pago está pendiente de confirmación",
      message:
        "Stripe todavía no nos permite confirmar el pedido automáticamente. No realices un segundo pago.",
      note:
        "Conservamos tu carrito. Si el pago se completa, recibirás la confirmación por email.",
    },
    error: {
      icon: "!",
      title: "No podemos confirmar el pago",
      message:
        "No hemos podido verificar este pedido de forma segura. Tu carrito no se ha vaciado.",
      note:
        "Si has visto el cargo en Stripe, contacta con nosotros antes de volver a pagar.",
    },
  }[paymentStatus];

  return (
    <>
      <SEO
        title="Pedido recibido | Imalandia"
        description="Confirmación de pedido recibido correctamente en Imalandia."
        canonical={`${SITE_URL}/success`}
        noindex
      />

      <Header />

      <main className="checkout-result-page">
        <section
          className={`checkout-result-card checkout-result-card--${paymentStatus}`}
          aria-live="polite"
          aria-busy={paymentStatus === "checking"}
        >
          <span className="checkout-result-icon" aria-hidden="true">
            {content.icon}
          </span>

          <h1>{content.title}</h1>

          <p>{content.message}</p>

          <p className="checkout-result-note">{content.note}</p>

          {paymentStatus !== "checking" && (
            <div className="checkout-result-actions">
              {paymentStatus === "error" && (
                <Link to="/contacto" className="checkout-result-link">
                  Contactar con Imalandia
                </Link>
              )}

              <Link to="/" className="checkout-result-link">
                Volver al inicio
              </Link>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}
